import { useQuery } from "@apollo/client"
import { useState } from "react"
import { Control, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { gql } from "~/__generated__"
import tag10 from "~/images/icons/tag-10"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { Tag } from "~/ui/tag"

const getUserTagsQuery = gql(/* GraphQL */ `
  query GetUserTags {
    viewer {
      tags {
        id
        name
      }
    }
  }
`)

export interface TagFieldTag {
  id: string
  name: string
  isNew?: boolean
}

interface TagFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label?: string
  placeholder?: string
  selectedTags: TagFieldTag[]
  onTagsChange: (tags: TagFieldTag[]) => void
  disabled?: boolean
  hideLabel?: boolean
}

export function TagField<T extends FieldValues>({
  control,
  name,
  label = "Tags",
  placeholder = "",
  selectedTags,
  onTagsChange,
  disabled = false,
  hideLabel = false,
}: TagFieldProps<T>) {
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const { setValue, watch } = useFormContext<T>()

  const { data: tagsData, loading: tagsLoading } = useQuery(getUserTagsQuery)
  const userTags = tagsData?.viewer?.tags || []

  const handleTagSelect = (tag: { id: string; name: string }) => {
    if (!selectedTags.some((t) => t.id === tag.id)) {
      onTagsChange([...selectedTags, tag])
    }
    setValue(name, "" as any)
    setShowTagSuggestions(false)
  }

  const handleTagRemove = (tagId: string) => {
    onTagsChange(selectedTags.filter((tag) => tag.id !== tagId))
  }

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const tagInput = watch(name)?.trim()
      if (
        tagInput &&
        !selectedTags.some((tag) => tag.name.toLowerCase() === tagInput.toLowerCase())
      ) {
        const newTag = {
          id: `new-${Date.now()}`,
          name: tagInput,
          isNew: true,
        }
        onTagsChange([...selectedTags, newTag])
        setValue(name, "" as any)
        setShowTagSuggestions(false)
      }
    }
  }

  const getFilteredTags = (inputValue: string) => {
    if (!inputValue.trim())
      return userTags.filter((tag) => !selectedTags.some((t) => t.id === tag.id))
    return userTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.some((t) => t.id === tag.id)
    )
  }

  const createNewTag = (tagInput: string) => {
    if (
      tagInput &&
      !selectedTags.some((tag) => tag.name.toLowerCase() === tagInput.toLowerCase())
    ) {
      const newTag = {
        id: `new-${Date.now()}`,
        name: tagInput,
        isNew: true,
      }
      onTagsChange([...selectedTags, newTag])
      setValue(name, "" as any)
    }
    setShowTagSuggestions(false)
  }

  return (
    <div className="space-y-3">
      {!hideLabel && <FormLabel>{label}</FormLabel>}

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <Tag key={tag.id} selected={true} className="flex items-center gap-1">
              {tag.name}
              <button
                type="button"
                onClick={() => handleTagRemove(tag.id)}
                className="ml-1 text-white hover:text-gray-200"
                disabled={disabled}
              >
                ×
              </button>
            </Tag>
          ))}
        </div>
      )}

      {/* Tag Input */}
      <div className="relative">
        <FormField
          control={control}
          name={name}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder={placeholder}
                  {...field}
                  onKeyDownCapture={() => {
                    if (!showTagSuggestions) setShowTagSuggestions(true)
                  }}
                  onClick={() => setShowTagSuggestions(true)}
                  onFocus={() => setShowTagSuggestions(true)}
                  onBlur={(e) => {
                    // Check if the related target (where focus is going) is within the dropdown
                    const relatedTarget = e.relatedTarget as HTMLElement
                    const dropdown =
                      e.currentTarget.parentElement?.querySelector("[data-tag-dropdown]")

                    if (!relatedTarget || !dropdown?.contains(relatedTarget)) {
                      setShowTagSuggestions(false)
                    }
                  }}
                  onKeyDown={handleTagInputKeyDown}
                  disabled={disabled || tagsLoading}
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Tag Suggestions Dropdown */}
        {showTagSuggestions && !disabled && (
          <div
            data-tag-dropdown
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-md border bg-popover p-1 shadow-md"
          >
            {getFilteredTags(watch(name) || "").length > 0 ? (
              <div className="space-y-1">
                {getFilteredTags(watch(name) || "").map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleTagSelect(tag)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 hover:bg-gray-100"
                  >
                    <img {...tag10} alt="" />
                    {tag.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">No tags found</div>
            )}

            {/* Add new tag option */}
            {(watch(name) || "").trim() &&
              !userTags.some(
                (tag) => tag.name.toLowerCase() === (watch(name) || "").toLowerCase()
              ) && (
                <div className="border-t pt-1">
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      const tagInput = (watch(name) || "").trim()
                      createNewTag(tagInput)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 hover:bg-gray-100"
                  >
                    <span className="text-xs">➕</span>
                    Create "{watch(name)}"
                  </button>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}
